/**
 * Coming back to a Sketch from Buy Lux (pure, tested in purchase.test.ts), as the Permit workspace does (#118):
 * the Lux confirmations of "Design with AI" and the Assist helpers carry a return path to this Sketch with the action
 * to offer again; before leaving, the drawing is saved; after paying, the Sketch says "You now have N Lux · Design
 * with AI?" (or the helper) once the purchase has reached the wallet.
 *
 * Spendable Lux: "Design with AI" is an automatic design, so the Lux reserved for designs counts; the Assist helpers
 * may not use it (`spendableLux(wallet, false)`).
 */
import type { MessageKey } from "../i18n/keys";
import type { Text } from "./text.ts";
import { spendableLux } from "../lib/luxSpend.ts";

export type ResumeAction = "ai" | "roof_hints" | "surroundings";
const ACTIONS: ResumeAction[] = ["ai", "roof_hints", "surroundings"];
export const ACTION_LABEL: Record<ResumeAction, MessageKey> = { ai: "sketch.action.ai", roof_hints: "sketch.action.roofHints", surroundings: "sketch.action.surroundings" };

/** Where checkout comes back to: this Sketch, offering `action` again (LuxSpend adds `checkout=paid|canceled`). */
export const sketchReturnPath = (designId: string, action: ResumeAction) => `/designs/${designId}/sketch?resume=${action}`;

/** The action to offer again from the URL's `resume`, or null. */
export function resumeOf(raw: string | null | undefined): ResumeAction | null {
  return ACTIONS.includes(raw as ResumeAction) ? (raw as ResumeAction) : null;
}

/** Whether an action may use the Lux reserved for automatic designs. */
export const usesReserved = (action: ResumeAction) => action === "ai";

/** The Lux an action may spend, from the wallet. */
export function spendableFor(action: ResumeAction, wallet: Parameters<typeof spendableLux>[0]): number | null {
  return spendableLux(wallet, usesReserved(action));
}

export interface SketchPurchasePrompt {
  waiting: boolean;
  title: string;
  detail: string;
  /** The action can be paid now. */
  canRun: boolean;
  /** The button: "Design with AI · 1.00 Lux". */
  action: string;
}

/** After a purchase: "Adding your Lux…" until it arrives, then "You now have N Lux · Design with AI?". */
export function sketchPurchasePrompt(tx: Text, action: ResumeAction, spendable: number | null | undefined, cost: number, arrived: boolean): SketchPurchasePrompt {
  const label = tx.t(ACTION_LABEL[action]);
  const button = cost > 0 ? tx.t("sketch.purchase.button", { label, lux: tx.lux(cost) }) : label;
  if (!arrived || spendable == null) return { waiting: true, title: tx.t("sketch.purchase.received"), detail: tx.t("sketch.purchase.adding"), canRun: false, action: button };
  const canRun = cost <= 0 || spendable >= cost;
  return {
    waiting: false,
    title: tx.t("sketch.purchase.nowHave", { lux: tx.lux(spendable) }),
    detail: canRun ? (cost > 0 ? tx.t("sketch.purchase.runPaid", { label, lux: tx.lux(cost) }) : tx.t("sketch.purchase.runIncluded", { label })) : tx.t("sketch.purchase.notEnough", { label, lux: tx.lux(cost) }),
    canRun,
    action: button,
  };
}
